<template>
  <v-navigation-drawer
    v-if="featureEnabled"
    right
    app
    temporary
    :value="drawerOpen"
    class="pay-range-draft-drawer"
    :style="{ width: '80vw' }"
    @input="onDrawerInput"
  >
    <div class="pay-range-draft-drawer__panel">
      <PayRangeDrawerHeader
        :step="step"
        class="pay-range-draft-drawer__header"
        :title="headerTitle"
        :subtitle="headerSubtitle"
        :version-code="versionLabel"
        :status="status"
        :state-badge="stateBadge"
        :effective-from="effectiveFrom"
        :show-badges="true"
        :show-state-badge="step === 2"
        :show-period-bar="step === 2"
        :period-bar-state="'draft'"
        :period-bar-label="periodLabel"
        :period-bar-outlined="true"
        :open-ended="true"
        @close="requestClose"
        @chatter="$emit('chatter')"
      />

      <PayRangeDraftActionBar
        :step="step"
        :state="actionBarState"
        :identity-valid="identityValid"
        :amounts-valid="amountsValid"
        :suggest-enabled="suggestEnabled"
        :publishing="publishing"
        @discard="onDiscardDraft"
        @next="onNext"
        @back="onBack"
        @suggest="onSuggest"
        @save-draft="onSave"
        @publish="onPublish"
        @stop-sourcing="onStopSourcing"
      />

      <div class="pay-range-draft-drawer__body">
        <div
          class="pay-range-draft-drawer__banner"
          role="note"
          :data-test="step === 1 ? 'identity-banner' : 'amounts-banner'"
        >
          <span class="pay-range-draft-drawer__banner-icon" aria-hidden="true">
            <v-icon size="20" color="#ffffff">mdi-information-outline</v-icon>
          </span>
          <p class="pay-range-draft-drawer__banner-text">{{ bannerText }}</p>
        </div>

        <!-- Step 1 · Identity -->
        <template v-if="step === 1">
          <PayRangeIdentityFields
            :model-value="identity"
            :countries="countries"
            :currencies="currencies"
            :job-families="jobFamilies"
            :region-items="regionItems"
            :sub-region-items="subRegionItems"
            :loading-currency="currencyLoading"
            :loading-location="locationLoading"
            :duplicate-message="duplicateMessage"
            :pay-basis-options="payBasisOptions"
            :work-mode-options="workModeOptions"
            @update:modelValue="onIdentityChange"
            @search-job-family="$emit('search-job-family', $event)"
            @search-location="onSearchLocation"
            @clear-location="$emit('clear-location')"
            @search-currency="$emit('search-currency', $event)"
            @add-job-family="$emit('add-job-family')"
          />
        </template>

        <!-- Step 2 · Amounts -->
        <template v-else>
          <PayRangeIdentitySummary
            :identity="identity"
            :pay-basis-options="payBasisOptions"
            :work-mode-options="workModeOptions"
          />

          <div class="pay-range-draft-drawer__section">
            <EffectiveDateToEffectiveTo
              :effective-from="effectiveFrom"
              :effective-to="effectiveTo"
              :error="effectiveFromError"
              @update:effectiveFrom="onEffectiveFromChange"
              @update:effectiveTo="onEffectiveToChange"
            />
          </div>

          <div class="pay-range-draft-drawer__section">
            <PayRangeAmountsEditor
              :min-amount="minAmount"
              :max-amount="maxAmount"
              :currency-code="currencyCode"
              :decimal-places="decimalPlaces"
              :pay-basis="identity.payBasis || 'ANNUAL'"
              layout="timeline"
              :state="amountsState"
              :suggestion="suggestion"
              :sources="sources"
              :requested-scope-level="requestedScopeLevel"
              :effective-scope-level="effectiveScopeLevel"
              :reasoning="sourcingReasoning"
              :phase="sourcingPhase"
              :stalled="sourcingStalled"
              :sourcing-started-at="sourcingStartedAt"
              @update:minAmount="onMinChange"
              @update:maxAmount="onMaxChange"
              @try-again="onSuggest"
              @enter-manually="onEnterManually"
              @undo-suggestion="onUndoSuggestion"
            />
          </div>

          <div v-if="saveError" class="pay-range-draft-drawer__alert pay-range-draft-drawer__alert--error" role="alert" data-test="save-error">
            <v-icon small color="#b42318">mdi-alert-circle-outline</v-icon>
            <span>{{ saveError }}</span>
          </div>
          <div v-if="publishError" class="pay-range-draft-drawer__alert pay-range-draft-drawer__alert--error" role="alert" data-test="publish-error">
            <v-icon small color="#b42318">mdi-alert-circle-outline</v-icon>
            <span>{{ publishError }}</span>
          </div>
          <div v-if="savedMessage" class="pay-range-draft-drawer__alert pay-range-draft-drawer__alert--success" role="status" data-test="saved-alert">
            <v-icon small color="#067647">mdi-check-circle-outline</v-icon>
            <span>Draft saved. Nothing changes for positions until you publish.</span>
          </div>
        </template>
      </div>
    </div>

    <PayRangeUnsavedChanges
      :value="unsavedDialog"
      @input="unsavedDialog = $event"
      @keep-editing="unsavedDialog = false"
      @discard="onDiscardUnsaved"
    />

    <DiscardDraftDialog
      :value="discardDialog"
      @input="discardDialog = $event"
      @onAction="onConfirmDiscardDraft"
    />
  </v-navigation-drawer>
</template>

<script setup>
import dayjs from "dayjs";
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import PayRangeDrawerHeader from "@components/PayRangeDrawerHeader/PayRangeDrawerHeader.vue";
import PayRangeDraftActionBar from "@components/PayRangeDraftActionBar/PayRangeDraftActionBar.vue";
import PayRangeIdentityFields from "@components/PayRangeIdentityFields/PayRangeIdentityFields.vue";
import PayRangeIdentitySummary from "@components/PayRangeIdentitySummary/PayRangeIdentitySummary.vue";
import EffectiveDateToEffectiveTo from "@components/EffectiveDateToEffectiveTo/EffectiveDateToEffectiveTo.vue";
import PayRangeAmountsEditor from "@components/PayRangeAmountsEditor/PayRangeAmountsEditor.vue";
import PayRangeUnsavedChanges from "@components/PayRangeUnsavedChanges/PayRangeUnsavedChanges.vue";
import DiscardDraftDialog from "@components/DiscardDraftDialog/DiscardDraftDialog.vue";
import {
  suggestPayRange,
  updatePayRangeDraft,
  publishPayRange,
  payRangeLifecycleAction,
} from "@/api/payRangeAPI";
import {
  payRangeApiToModalInitialValues,
  toApiDateString,
  toDraftApiRequest,
  toSuggestApiRequest,
} from "@components/PayRangeModal/payRangeDraftPayload";
import { formatLevelLabel } from "@components/JobFamilyPickerDialog/jobFamilyPickerDialogUtils";
import { versionStatusBadge } from "@components/FairPay/payRangeBadges";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { formatPayRangeBarLabel, formatPayRangeWholeAmount } from "@components/utils/formatPayRangeAmount";
import { useBodyPortalPointerTracker } from "@components/utils/useBodyPortalDropdown";

const IDENTITY_BANNER_TEXT =
  "Choose the job family level, location, currency, pay basis, work mode and when the range takes effect. " +
  "The first version is a Draft: no position uses it until you publish.";
const AMOUNTS_BANNER_TEXT =
  "Set when the first version takes effect and its Min and Max. Effective from is required to publish.";

const props = defineProps({
  value: { type: Boolean, default: false },
  /** Draft pay range API object (list/detail serializer). */
  payRange: { type: Object, default: () => ({}) },
  step: { type: Number, default: 1 },
  countries: { type: Array, default: () => [] },
  currencies: { type: Array, default: () => [] },
  jobFamilies: { type: Array, default: () => [] },
  regionItems: { type: Array, default: () => [] },
  subRegionItems: { type: Array, default: () => [] },
  currencyLoading: { type: Boolean, default: false },
  locationLoading: { type: Boolean, default: false },
  payBasisOptions: { type: Array, default: () => [] },
  workModeOptions: { type: Array, default: () => [] },
  /** Overridable async handlers (Storybook / tests). Default to the real API. */
  suggest: { type: Function, default: null },
  saveDraft: { type: Function, default: null },
  publish: { type: Function, default: null },
  discard: { type: Function, default: null },
});

const emit = defineEmits([
  "update:value",
  "close",
  "save",
  "publish",
  "discard",
  "chatter",
  "search-job-family",
  "search-location",
  "clear-location",
  "search-currency",
  "add-job-family",
]);

const { proxy } = getCurrentInstance() || {};

const step = ref(props.step);
const identity = ref({});
const minAmount = ref(null);
const maxAmount = ref(null);
const effectiveFrom = ref(null);
const effectiveTo = ref(null);
const sources = ref([]);
const suggestion = ref(null);
const previousAmounts = ref(null);
const requestedScopeLevel = ref(null);
const effectiveScopeLevel = ref(null);

const suggestState = ref("idle");
const sourcingReasoning = ref("");
const sourcingPhase = ref("");
const sourcingStalled = ref(false);
const sourcingStartedAt = ref(null);
const saveError = ref("");
const publishError = ref("");
const savedMessage = ref(false);
const duplicateMessage = ref("");
const effectiveFromError = ref("");
const saving = ref(false);
const publishing = ref(false);
const dirty = ref(false);
const drawerOpen = ref(props.value);
const unsavedDialog = ref(false);
const { consumePortalPointer } = useBodyPortalPointerTracker();
const discardDialog = ref(false);

let abortController = null;
let seeded = false;

const featureEnabled = ref(false);
const suggestEnabled = featureEnabled;

const payRangeId = computed(() => props.payRange?.id ?? props.payRange?.pay_range_id ?? null);
const versionCode = computed(() => props.payRange?.code || props.payRange?.pay_range_code || null);
const status = computed(() => versionStatusBadge("draft").label);
const stateBadge = computed(() => "Future");
// A never-published range has no version number yet, so the badge reads "<code>.?".
const versionLabel = computed(() => (versionCode.value ? `${versionCode.value}.?` : null));

const headerTitle = computed(() => {
  const jobFamily = identity.value.jobFamily;
  if (!jobFamily?.jobFamilyName) return "Pay range";
  return jobFamily.level
    ? `${jobFamily.jobFamilyName} › ${formatLevelLabel(jobFamily.level)}`
    : jobFamily.jobFamilyName;
});
const headerSubtitle = computed(() => {
  const location = identity.value.location || {};
  return [versionCode.value, location.countryName, location.regionName, location.subRegionName]
    .filter(Boolean)
    .join(" · ");
});

const bannerText = computed(() => {
  if (step.value === 1) return IDENTITY_BANNER_TEXT;
  if (suggestState.value === "sourced" && suggestion.value) {
    const min = formatPayRangeWholeAmount(suggestion.value.min, currencyCode.value);
    const max = formatPayRangeWholeAmount(suggestion.value.max, currencyCode.value);
    return `IQ range applied: ${min} – ${max} from ${sources.value.length} market sources. You can still edit the amounts.`;
  }
  return AMOUNTS_BANNER_TEXT;
});

const periodLabel = computed(() => formatPayRangeBarLabel(minAmount.value, maxAmount.value, currencyCode.value));

const currencyCode = computed(
  () => identity.value.operatingCurrency?.code || props.payRange?.operating_currency?.code || "USD"
);
const decimalPlaces = computed(
  () => identity.value.operatingCurrency?.decimalPlaces ?? props.payRange?.operating_currency?.decimal_places ?? 2
);

const identityValid = computed(() => isIdentityComplete(identity.value));

const amountsValid = computed(() => {
  const min = Number(minAmount.value);
  const max = Number(maxAmount.value);
  return (
    minAmount.value != null &&
    minAmount.value !== "" &&
    maxAmount.value != null &&
    maxAmount.value !== "" &&
    Number.isFinite(min) &&
    Number.isFinite(max) &&
    min < max
  );
});

const drawerState = computed(() => {
  if (step.value === 1) return duplicateMessage.value ? "duplicate-identity" : "default";
  if (publishing.value) return "publishing";
  if (publishError.value) return "publish-failed";
  if (saveError.value) return "save-failed";
  if (savedMessage.value) return "saved";
  if (suggestState.value === "sourcing") return "sourcing";
  if (suggestState.value === "sourcing-stopped") return "sourcing-stopped";
  if (suggestState.value === "cannot-source") return "cannot-source";
  if (suggestState.value === "timed-out") return "timed-out";
  if (!amountsValid.value) return "max-must-exceed-min";
  if (suggestState.value === "sourced") return "iq-suggestion";
  return "default";
});

const actionBarState = computed(() => drawerState.value);
const amountsState = computed(() => {
  if (suggestState.value === "sourcing") return "sourcing";
  if (suggestState.value === "sourcing-stopped") return "sourcing-stopped";
  if (suggestState.value === "cannot-source") return "cannot-source";
  if (suggestState.value === "timed-out") return "timed-out";
  if (!amountsValid.value) return "max-must-exceed-min";
  if (suggestState.value === "sourced") return "iq-suggestion";
  return "default";
});

function isIdentityComplete(identity) {
  const jobFamilyLevelId =
    identity.jobFamilyLevelId ?? identity.jobFamily?.levelId ?? identity.jobFamily?.level?.id ?? null;
  const location = identity.location || {};
  if (jobFamilyLevelId == null) return false;
  if (!location.countryCode) return false;
  if (location.scope === "metro" || location.scope === "county") {
    if (location.subRegionId == null) return false;
  } else if (location.scope === "region-wide" || location.scope === "specific") {
    if (location.regionId == null) return false;
  }
  if (!identity.operatingCurrency?.id && !identity.operatingCurrency?.code) return false;
  if (!identity.payBasis) return false;
  if (!identity.workMode) return false;
  return true;
}

function seed() {
  const initial = payRangeApiToModalInitialValues(props.payRange || {});
  identity.value = {
    jobFamily: initial.jobFamily || null,
    jobFamilyLevelId: initial.jobFamilyLevelId ?? null,
    location: initial.location || null,
    operatingCurrency: initial.operatingCurrency || null,
    payBasis: initial.payBasis || null,
    workMode: initial.workMode || "UNSPECIFIED",
  };
  minAmount.value = initial.minAmount ?? null;
  maxAmount.value = initial.maxAmount ?? null;
  effectiveFrom.value = initial.effectiveFrom || dayjs().format("YYYY-MM-DD");
  effectiveTo.value = initial.effectiveTo || null;
  clearSuggestion();
  suggestState.value = "idle";
  saveError.value = "";
  publishError.value = "";
  savedMessage.value = false;
  duplicateMessage.value = "";
  effectiveFromError.value = "";
  seeded = true;
  dirty.value = false;
}

onMounted(seed);
onMounted(() => document.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
  if (abortController) abortController.abort();
});
onMounted(async () => {
  try {
    if (proxy && typeof proxy.$getFlag === "function") {
      featureEnabled.value = (await proxy.$getFlag(LD_FLAG_IDEA_MDP_429, false)) === true;
    }
  } catch {
    featureEnabled.value = false;
  }
});
watch(() => props.payRange, seed, { deep: true });
watch(
  () => props.value,
  (v) => {
    drawerOpen.value = v;
  }
);
watch(
  () => props.step,
  (v) => {
    step.value = v;
  }
);

/** The search payload carries the drawer's current location so the parent can scope region / sub-region lookups. */
function onSearchLocation(payload) {
  emit("search-location", { ...payload, location: identity.value.location || null });
}

function onIdentityChange(next) {
  identity.value = next;
  duplicateMessage.value = "";
  markDirty();
}

function onEffectiveFromChange(v) {
  effectiveFrom.value = toApiDateString(v);
  effectiveFromError.value = "";
  markDirty();
}

function onEffectiveToChange(v) {
  effectiveTo.value = toApiDateString(v);
  markDirty();
}

function onMinChange(v) {
  minAmount.value = v;
  saveError.value = "";
  publishError.value = "";
  markDirty();
}

function onMaxChange(v) {
  maxAmount.value = v;
  saveError.value = "";
  publishError.value = "";
  markDirty();
}

function markDirty() {
  if (seeded) dirty.value = true;
}

function onNext() {
  if (!identityValid.value) return;
  duplicateMessage.value = "";
  step.value = 2;
}

function onBack() {
  if (suggestState.value === "sourcing") {
    if (abortController) abortController.abort();
    abortController = null;
    suggestState.value = "idle";
  }
  step.value = 1;
}

function onKeydown(event) {
  if (event.key !== "Escape" || !drawerOpen.value) return;
  if (unsavedDialog.value || discardDialog.value) return;
  requestClose();
}

function onDrawerInput(open) {
  if (open) {
    drawerOpen.value = true;
    return;
  }
  if (consumePortalPointer()) {
    // A picker option list portaled to <body> counts as "outside" for Vuetify; it is part of the form.
    drawerOpen.value = false;
    nextTick(() => {
      drawerOpen.value = true;
    });
    return;
  }
  if (!dirty.value) {
    close();
    return;
  }
  // Click-outside asked to close while there are unsaved changes: keep the drawer open and ask first.
  unsavedDialog.value = true;
  drawerOpen.value = false;
  nextTick(() => {
    drawerOpen.value = true;
  });
}

function requestClose() {
  if (dirty.value) {
    unsavedDialog.value = true;
    return;
  }
  close();
}

function onDiscardUnsaved() {
  unsavedDialog.value = false;
  dirty.value = false;
  close();
}

function close() {
  if (abortController) abortController.abort();
  drawerOpen.value = false;
  emit("update:value", false);
  emit("close");
}

function onDiscardDraft() {
  discardDialog.value = true;
}

async function onConfirmDiscardDraft() {
  discardDialog.value = false;
  if (abortController) abortController.abort();
  try {
    const handler = props.discard || ((id) => payRangeLifecycleAction(id, "delete-draft", {}, "delete"));
    await handler(payRangeId.value);
  } catch {
    /* discard failure is surfaced by the parent */
  }
  dirty.value = false;
  emit("discard");
  close();
}

async function onSuggest() {
  if (step.value !== 2) return;
  suggestState.value = "sourcing";
  sourcingReasoning.value = "";
  sourcingPhase.value = "";
  sourcingStalled.value = false;
  sourcingStartedAt.value = Date.now();
  saveError.value = "";
  publishError.value = "";
  savedMessage.value = false;
  const controller = new AbortController();
  abortController = controller;
  const isCurrent = () => abortController === controller && !controller.signal.aborted;

  const seed = toSuggestApiRequest({ ...identity.value, minAmount: minAmount.value, maxAmount: maxAmount.value });
  try {
    const handler = props.suggest || suggestPayRange;
    const res = await handler(seed, {
      signal: controller.signal,
      onThinking: (text) => {
        if (!isCurrent()) return;
        sourcingReasoning.value = text || "";
        sourcingStalled.value = false;
      },
      onPhase: (label) => {
        if (!isCurrent()) return;
        sourcingPhase.value = label || "";
        sourcingStalled.value = false;
      },
      onStalled: () => {
        if (isCurrent()) sourcingStalled.value = true;
      },
    });
    if (controller.signal.aborted) return;
    const data = res?.data ?? res ?? {};
    const list = Array.isArray(data.sources) ? data.sources : data?.results?.sources || [];
    const suggested = data.suggestion || null;

    if (!suggested || (suggested.min == null && suggested.max == null) || list.length === 0) {
      suggestState.value = "cannot-source";
      return;
    }
    if (Number(suggested.min) >= Number(suggested.max)) {
      suggestState.value = "cannot-source";
      return;
    }
    if (!previousAmounts.value) previousAmounts.value = { min: minAmount.value, max: maxAmount.value };
    sources.value = list;
    suggestion.value = suggested;
    requestedScopeLevel.value = data.requested_scope_level ?? data.requestedScopeLevel ?? null;
    effectiveScopeLevel.value = data.effective_scope_level ?? data.effectiveScopeLevel ?? null;
    minAmount.value = suggested.min;
    maxAmount.value = suggested.max;
    markDirty();
    suggestState.value = "sourced";
  } catch (err) {
    if (controller.signal.aborted && abortController !== controller) return;
    if (controller.signal.aborted || isAbort(err)) {
      suggestState.value = "sourcing-stopped";
    } else {
      suggestState.value = "timed-out";
    }
  } finally {
    if (abortController === controller) abortController = null;
  }
}

function onStopSourcing() {
  if (abortController) abortController.abort();
  suggestState.value = "sourcing-stopped";
}

function clearSuggestion() {
  suggestion.value = null;
  sources.value = [];
  previousAmounts.value = null;
  requestedScopeLevel.value = null;
  effectiveScopeLevel.value = null;
}

function onEnterManually() {
  suggestState.value = "idle";
  clearSuggestion();
}

function onUndoSuggestion() {
  if (previousAmounts.value) {
    minAmount.value = previousAmounts.value.min;
    maxAmount.value = previousAmounts.value.max;
  }
  clearSuggestion();
  suggestState.value = "idle";
  markDirty();
}

async function onSave() {
  if (!amountsValid.value) return;
  saving.value = true;
  saveError.value = "";
  publishError.value = "";
  savedMessage.value = false;
  try {
    const handler = props.saveDraft || updatePayRangeDraft;
    await handler(payRangeId.value, buildDraftPayload());
    savedMessage.value = true;
    dirty.value = false;
    emit("save");
  } catch (err) {
    const code = extractErrorCode(err);
    if (code === "PAY_RANGE_DUPLICATE_IDENTITY") {
      duplicateMessage.value = duplicateIdentityMessage(err);
      step.value = 1;
    } else if (code === "PAY_RANGE_VERSION_MAX_NOT_ABOVE_MIN") {
      saveError.value = "Max must exceed Min.";
    } else {
      saveError.value = "Save failed. Your entries are kept. Try again.";
    }
  } finally {
    saving.value = false;
  }
}

async function onPublish() {
  if (!effectiveFrom.value) {
    effectiveFromError.value = "Effective from is required.";
    return;
  }
  if (!amountsValid.value) return;
  publishing.value = true;
  publishError.value = "";
  saveError.value = "";
  savedMessage.value = false;
  try {
    // Stage what is on screen first (amounts and dates) so stale values never go live.
    const stage = props.saveDraft || updatePayRangeDraft;
    await stage(payRangeId.value, buildDraftPayload());
    const handler = props.publish || publishPayRange;
    await handler(payRangeId.value, {
      effective_from: toApiDateString(effectiveFrom.value),
      effective_to: toApiDateString(effectiveTo.value),
    });
    dirty.value = false;
    emit("publish");
    close();
  } catch (err) {
    const code = extractErrorCode(err);
    if (code === "PAY_RANGE_VERSION_MAX_NOT_ABOVE_MIN") {
      publishError.value = "Max must exceed Min.";
    } else {
      publishError.value = "Publish failed. Your entries are kept. Try again.";
    }
  } finally {
    publishing.value = false;
  }
}

function buildDraftPayload() {
  return toDraftApiRequest({
    ...identity.value,
    minAmount: minAmount.value,
    maxAmount: maxAmount.value,
    sources: sources.value,
    effectiveFrom: effectiveFrom.value,
    effectiveTo: effectiveTo.value,
  });
}

function extractErrorCode(err) {
  const data = err?.response?.data;
  if (!data || typeof data === "string") return null;
  return data.code || data.error_code || data.error || data.detail?.code || null;
}

function duplicateIdentityMessage(err) {
  const data = err?.response?.data;
  const code = data?.details?.code || data?.details?.id || data?.code || null;
  return code ? `This identity already exists as ${code}. Change it to continue.` : "This identity already exists. Change it to continue.";
}

function isAbort(err) {
  if (!err) return false;
  return (
    err.name === "AbortError" ||
    err.code === "ERR_CANCELED" ||
    /cancel/i.test(err.message || "")
  );
}
</script>

<style scoped>
.pay-range-draft-drawer {
  height: 100vh;
  background-color: #ffffff;
}
.pay-range-draft-drawer__panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
}
.pay-range-draft-drawer__header {
  padding-top: 24px;
  padding-bottom: 12px;
}
.pay-range-draft-drawer__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.pay-range-draft-drawer__section {
  padding: 8px 0;
}
.pay-range-draft-drawer__banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
}
.pay-range-draft-drawer__banner-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background-color: #7f56d9;
  border-radius: 8px;
}
.pay-range-draft-drawer__banner-text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}
.pay-range-draft-drawer__alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}
.pay-range-draft-drawer__alert--error {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #b42318;
}
.pay-range-draft-drawer__alert--success {
  background-color: #e8f5e9;
  border: 1px solid #4caf50;
  color: #067647;
}
</style>
